import React, { useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useUserContext } from '../../context/UserContext';

export interface ParentInfo {
  fullName: string;
  email: string;
  phone: string;
  relationship: string;
}

interface CheckoutParentInfoProps {
  parentInfo: ParentInfo;
  onChange: (info: ParentInfo) => void;
  onBlur?: () => void;
  errors?: Partial<Record<keyof ParentInfo, string>>;
}

const RELATIONSHIPS = [
  { value: '', label: 'Select relationship' },
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'other', label: 'Other' },
];

const CheckoutParentInfo: React.FC<CheckoutParentInfoProps> = ({
  parentInfo,
  onChange,
  onBlur,
  errors = {},
}) => {
  const { user, isLoggedIn } = useUserContext();

  // Prefill name and email from logged-in user when fields are empty
  useEffect(() => {
    if (isLoggedIn && user) {
      const updates: Partial<ParentInfo> = {};
      if (user.email && !parentInfo.email) {
        updates.email = user.email;
      }
      if (user.name && !parentInfo.fullName) {
        updates.fullName = user.name;
      }
      if (Object.keys(updates).length > 0) {
        onChange({
          ...parentInfo,
          ...updates,
        });
      }
    }
  }, [isLoggedIn, user?.email, user?.name]);

  const handleChange = (field: keyof ParentInfo, value: string) => {
    onChange({
      ...parentInfo,
      [field]: value,
    });
  };

  return (
    <div className="checkout-parent-info">
      <p className="checkout-section-description">
        Please provide your contact details.
      </p>

      <div className="checkout-form-row">
        <div className="form-group checkout-form-group-half">
          <label htmlFor="parent-name">Your Full Name</label>
          <input
            type="text"
            id="parent-name"
            className={`form-input ${errors.fullName ? 'form-input--error' : ''}`}
            value={parentInfo.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            onBlur={onBlur}
            placeholder="Full Name"
            required
          />
          {errors.fullName && (
            <span className="form-error">{errors.fullName}</span>
          )}
        </div>

        <div className="form-group checkout-form-group-half">
          <label htmlFor="parent-email">Email Address</label>
          <input
            type="email"
            id="parent-email"
            className={`form-input ${errors.email ? 'form-input--error' : ''}`}
            value={parentInfo.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={onBlur}
            placeholder="name@example.com"
            required
          />
          {errors.email && (
            <span className="form-error">{errors.email}</span>
          )}
        </div>
      </div>

      <div className="checkout-form-row">
        <div className="form-group checkout-form-group-half">
          <label htmlFor="parent-phone">Phone Number</label>
          <input
            type="tel"
            id="parent-phone"
            className={`form-input ${errors.phone ? 'form-input--error' : ''}`}
            value={parentInfo.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            onBlur={onBlur}
            placeholder="(123) 456-7890"
            required
          />
          {errors.phone && (
            <span className="form-error">{errors.phone}</span>
          )}
        </div>

        <div className="form-group checkout-form-group-half">
          <label htmlFor="parent-relationship">Relationship to Child</label>
          <div className="form-select-wrapper">
            <select
              id="parent-relationship"
              className={`form-input form-select ${errors.relationship ? 'form-input--error' : ''}`}
              value={parentInfo.relationship}
              onChange={(e) => handleChange('relationship', e.target.value)}
              onBlur={onBlur}
              required
            >
              {RELATIONSHIPS.map((rel) => (
                <option key={rel.value} value={rel.value}>
                  {rel.label}
                </option>
              ))}
            </select>
            <ChevronDown className="form-select-icon icon icon--sm" />
          </div>
          {errors.relationship && (
            <span className="form-error">{errors.relationship}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutParentInfo;
